import dayjs from "dayjs";

import {
  OrthoApprovalStatus,
  TaskStatus,
  type OrthoStatusResponse,
  type TaskResponse,
} from "api/model";
import type { LocationOrtho } from "context/CogOrtho/cogOrthoContext";
import type { ImageCornerCoordinates } from "context/CogOrtho/orthoAssets";
import { isStitchedOrthoStatus } from "context/CogOrtho/orthoStatus";

import {
  getTaskOrthoId,
  groupTasksByFolder,
  isInFlightSession,
  type OrthoTaskFolderGroup,
  type OrthoUploadSession,
} from "./orthoUpload";
import {
  getPlotTaskOrthoId,
  getPlotTaskTrialId,
  getRetryableTaskMessage,
  isRetryableTask,
} from "./orthoPlotTasks";

// --- Keys ---

/** Opaque workflow identity. Build with `getWorkflowKey`; never parse. */
export type OrthoWorkflowKey = string & {
  readonly __brand: "OrthoWorkflowKey";
};

type WorkflowKeySource =
  | { orthoId: number }
  /** A new upload in this tab, before it has been registered with the API. */
  | { sessionId: string };

/** The only place that knows how workflow keys are derived. */
export const getWorkflowKey = (src: WorkflowKeySource): OrthoWorkflowKey =>
  ("orthoId" in src
    ? `ortho:${src.orthoId}`
    : `session:${src.sessionId}`) as OrthoWorkflowKey;

/** Local calendar day (`YYYY-MM-DD`) for the date field. */
export const formatFlightDate = (date: Date): string =>
  dayjs(date).format("YYYY-MM-DD");

/** Local clock time (`HH:mm:ss`) from an image capture timestamp. */
export const formatFlightTime = (date: Date): string =>
  dayjs(date).format("HH:mm:ss");

/** Flight instant sent when creating an ortho and its image uploads, with the local offset. */
export const formatFlightDateTime = (date: Date): string =>
  dayjs(date).format("YYYY-MM-DDTHH:mm:ssZ");

/** An ortho's `flight_date` as YYYY-MM-DD. */
export const getOrthoFlightDate = (ortho: { flight_date: string }): string =>
  ortho.flight_date.slice(0, 10);

/** An ortho's `flight_date` as local HH:mm:ss. */
export const getOrthoFlightTime = (ortho: { flight_date: string }): string => {
  const parsed = dayjs(ortho.flight_date);
  return parsed.isValid() ? parsed.format("HH:mm:ss") : "00:00:00";
};

/** Calendar day, plus local time when another flight shares that day. */
export const formatOrthoFlightLabel = (
  flightDate: string,
  flightTime: string | null,
  sharesDate: boolean,
): string =>
  sharesDate && flightTime != null ? `${flightDate} ${flightTime}` : flightDate;

interface WorkflowIdentity {
  key: OrthoWorkflowKey;
  siteId: number | null;
  flightDate: string | null;
  flightTime: string | null;
}

const identityForOrtho = (ortho: LocationOrtho): WorkflowIdentity => ({
  key: getWorkflowKey({ orthoId: ortho.id }),
  siteId: ortho.site_id,
  flightDate: getOrthoFlightDate(ortho),
  flightTime: getOrthoFlightTime(ortho),
});

const identityForSession = (
  session: OrthoUploadSession,
): WorkflowIdentity | null => {
  const flightDate =
    session.flightDateTime != null
      ? formatFlightDate(session.flightDateTime)
      : null;
  const flightTime =
    session.flightDateTime != null
      ? formatFlightTime(session.flightDateTime)
      : null;
  // The placeholder created in this tab is already the workflow.
  if (session.orthoId != null) {
    return {
      key: getWorkflowKey({ orthoId: session.orthoId }),
      siteId: session.selectedSiteId,
      flightDate,
      flightTime,
    };
  }
  // A resume session continues its tasks' workflow, whatever the dialog picks.
  const resumeOrthoId =
    session.resumeTasks != null && session.resumeTasks.length > 0
      ? getTaskOrthoId(session.resumeTasks[0])
      : null;
  if (resumeOrthoId != null) {
    return {
      key: getWorkflowKey({ orthoId: resumeOrthoId }),
      siteId: session.selectedSiteId,
      flightDate,
      flightTime,
    };
  }
  // A site and flight date are required before this tab has a workflow.
  if (session.selectedSiteId == null || flightDate == null) {
    return null;
  }
  // A new upload in this tab, before it has been registered with the API.
  return {
    key: getWorkflowKey({ sessionId: session.id }),
    siteId: session.selectedSiteId,
    flightDate,
    flightTime,
  };
};

export const getOrthoWorkflowKey = (ortho: LocationOrtho): OrthoWorkflowKey =>
  getWorkflowKey({ orthoId: ortho.id });

// --- Shape ---

export type StepStatus = "not_started" | "in_progress" | "succeeded" | "failed";

export type AnalyzeStepName = "analyze_emergence" | "analyze_canopy";

export type WorkflowStepName =
  "upload" | "ortho_creation" | "review" | AnalyzeStepName;

export interface WorkflowError {
  step: WorkflowStepName;
  message: string;
  /** "task" = saved on the server; "client" = this tab only, lost on reload. */
  source: "task" | "client";
  at: string;
  /** The trial an analysis start failed for; absent for other steps. */
  trialId?: number;
}

export interface WorkflowStep {
  status: StepStatus;
  progress: number | null;
  error: WorkflowError | null;
}

export type OrthoWorkflowStage =
  | "selecting"
  | "hashing"
  | "uploading"
  | "upload_paused"
  | "upload_failed"
  | "ortho_creation_queued"
  | "ortho_creation_processing"
  /** The creation task failed, or the upload finished but no task was ever created. */
  | "ortho_creation_failed"
  | "awaiting_approval"
  | "rejected"
  | "approved";

/** Metadata for an already-created ortho; each field is absent until known. */
export interface ExistingOrthoMeta {
  /** COG footprint corners in WGS84. */
  boundary?: ImageCornerCoordinates;
  /** Ground sample distance; null once loaded but unavailable (non-projected CRS). */
  cmPerPx?: number | null;
}

export interface OrthoWorkflow {
  key: OrthoWorkflowKey;
  /** Null only for an upload that has not chosen a site yet. */
  siteId: number | null;
  /** YYYY-MM-DD. Null only for an upload that has not chosen a date yet. */
  flightDate: string | null;
  /** Local HH:mm:ss. Null only for an upload that has not chosen a time yet. */
  flightTime: string | null;

  /** Headline status: the step currently holding the flight up. */
  stage: OrthoWorkflowStage;
  steps: {
    upload: WorkflowStep;
    orthoCreation: WorkflowStep;
    review: WorkflowStep;
  };
  /** Errors from every step, newest first. */
  errors: WorkflowError[];

  upload: {
    session: OrthoUploadSession | null;
    batches: OrthoTaskFolderGroup[];
    uploadedCount: number;
    totalCount: number | null;
    pendingTasks: TaskResponse[];
    failedTasks: TaskResponse[];
  };
  orthoCreation: {
    /** Latest ortho_creation task. */
    task: TaskResponse | null;
    /** ScaleODM status while processing. */
    live: OrthoStatusResponse | null;
  };
  ortho: LocationOrtho | null;
  /** Emergence analysis of the ortho, from the latest analyze_emergence task per trial. */
  analyzeEmergence: WorkflowPlotTasks;
  /** Canopy cover analysis of the ortho, from the latest analyze_canopy_cover task per trial. */
  analyzeCanopy: WorkflowPlotTasks;
  meta: ExistingOrthoMeta;
}

export type ClientErrorsByKey = Readonly<
  Partial<Record<OrthoWorkflowKey, readonly WorkflowError[]>>
>;

// --- Task helpers ---

/** Latest `created_at` wins; the one rule for picking among tasks for the same thing. */
export const getLatestTask = (
  tasks: readonly TaskResponse[],
): TaskResponse | null =>
  tasks.reduce<TaskResponse | null>(
    (latest, task) =>
      latest == null || task.created_at > latest.created_at ? task : latest,
    null,
  );

const getTaskTime = (task: TaskResponse): string =>
  task.completed_at ?? task.created_at;

const newestFirst = (a: WorkflowError, b: WorkflowError): number =>
  b.at.localeCompare(a.at);

const getNewestError = (
  errors: readonly WorkflowError[],
  step: WorkflowStepName,
): WorkflowError | null =>
  errors
    .filter((error) => error.step === step)
    .reduce<WorkflowError | null>(
      (newest, error) =>
        newest == null || error.at >= newest.at ? error : newest,
      null,
    );

// --- Steps and stage ---

const getUploadTasksError = (
  failedTasks: readonly TaskResponse[],
): WorkflowError | null => {
  if (failedTasks.length === 0) {
    return null;
  }
  const at = failedTasks
    .map(getTaskTime)
    .reduce((latest, time) => (time > latest ? time : latest));
  return {
    step: "upload",
    message:
      failedTasks.length === 1
        ? "1 image failed to upload"
        : `${failedTasks.length} images failed to upload`,
    source: "task",
    at,
  };
};

const getOrthoCreationTaskErrors = (
  task: TaskResponse | null,
): WorkflowError[] =>
  task?.status === TaskStatus.failed
    ? (task.results?.errors ?? []).flatMap((error) => {
        const message = error.message?.trim();
        return message != null && message !== ""
          ? [
              {
                step: "ortho_creation" as const,
                message,
                source: "task" as const,
                at: error.timestamp ?? getTaskTime(task),
              },
            ]
          : [];
      })
    : [];

const getSessionError = (
  session: OrthoUploadSession | null,
): WorkflowError | null =>
  session?.errorMessage != null
    ? {
        step: "upload",
        message: session.errorMessage,
        source: "client",
        at: session.errorAt ?? "",
      }
    : null;

interface StageInput {
  session: OrthoUploadSession | null;
  ortho: LocationOrtho | null;
  creationTask: TaskResponse | null;
  pendingCount: number;
  failedCount: number;
  completedCount: number;
}

/**
 * Headline stage. An upload in flight wins; then a stitched ortho (so a few
 * failed images don't hide one awaiting approval); then the creation task.
 * `processing` means creation was requested and the image is not ready yet, so
 * the task still drives the stage. Then whatever is holding the upload up.
 */
export const deriveStage = ({
  session,
  ortho,
  creationTask,
  pendingCount,
  failedCount,
  completedCount,
}: StageInput): OrthoWorkflowStage => {
  if (session?.status === "hashing") {
    return "hashing";
  }
  if (session?.status === "uploading") {
    return "uploading";
  }
  // Uploading and processing rows are not reviewable. Processing means the
  // creation task was requested; the task (below) says whether it is queued,
  // running, or failed.
  if (ortho != null && isStitchedOrthoStatus(ortho.status)) {
    switch (ortho.status) {
      case OrthoApprovalStatus.approved:
        return "approved";
      case OrthoApprovalStatus.pending:
        return "awaiting_approval";
      case OrthoApprovalStatus.rejected:
        return "rejected";
    }
  }
  if (creationTask != null) {
    switch (creationTask.status) {
      case TaskStatus.failed:
        return "ortho_creation_failed";
      case TaskStatus.pending:
        return "ortho_creation_queued";
      default:
        // A completed task whose ortho is still processing reads as in progress.
        return "ortho_creation_processing";
    }
  }
  if (ortho?.status === OrthoApprovalStatus.processing) {
    // Requested, but the creation task has not loaded yet.
    return "ortho_creation_queued";
  }
  if (pendingCount > 0) {
    return "upload_paused";
  }
  if (completedCount > 0) {
    // Creation starts automatically after upload, so no task means that start failed.
    return "ortho_creation_failed";
  }
  if (failedCount > 0 || session?.status === "error") {
    return "upload_failed";
  }
  if (session?.status === "success") {
    return "ortho_creation_failed";
  }
  return "selecting";
};

/** A closed upload. Superseded tasks were released so those files can be uploaded again. */
export const isSettledUploadStatus = (status: TaskStatus): boolean =>
  status === TaskStatus.completed || status === TaskStatus.superseded;

const getUploadStepStatus = (
  session: OrthoUploadSession | null,
  tasks: readonly TaskResponse[],
  pendingCount: number,
  failedCount: number,
  hasDownstream: boolean,
): StepStatus => {
  if (isInFlightSession(session)) {
    return "in_progress";
  }
  if (failedCount > 0 || session?.status === "error") {
    return "failed";
  }
  if (
    pendingCount > 0 ||
    tasks.some((task) => !isSettledUploadStatus(task.status))
  ) {
    return "in_progress";
  }
  if (tasks.length > 0 || session?.status === "success" || hasDownstream) {
    return "succeeded";
  }
  return "not_started";
};

const getOrthoCreationStep = (
  ortho: LocationOrtho | null,
  task: TaskResponse | null,
  live: OrthoStatusResponse | null,
  error: WorkflowError | null,
  failedToStart: boolean,
): WorkflowStep => {
  if (ortho != null && isStitchedOrthoStatus(ortho.status)) {
    return { status: "succeeded", progress: 100, error: null };
  }
  const creationRequested = ortho?.status === OrthoApprovalStatus.processing;
  switch (task?.status) {
    case undefined:
      return {
        status: failedToStart
          ? "failed"
          : creationRequested
            ? "in_progress"
            : "not_started",
        progress: null,
        error,
      };
    case TaskStatus.failed:
      return { status: "failed", progress: null, error };
    case TaskStatus.waiting:
    case TaskStatus.pending:
      return { status: "in_progress", progress: null, error };
    case TaskStatus.processing:
      return { status: "in_progress", progress: live?.progress ?? null, error };
    // Ortho creation tasks aren't currently superseded during the workflow, but
    // if they ever are, treat them as a completed task that was replaced later.
    case TaskStatus.completed:
    case TaskStatus.superseded:
      return { status: "in_progress", progress: 100, error };
  }
};

const getReviewStep = (
  ortho: LocationOrtho | null,
  error: WorkflowError | null,
): WorkflowStep => {
  switch (ortho?.status) {
    case undefined:
    case OrthoApprovalStatus.processing:
    case OrthoApprovalStatus.uploading:
      return { status: "not_started", progress: null, error };
    case OrthoApprovalStatus.approved:
      return { status: "succeeded", progress: null, error };
    case OrthoApprovalStatus.pending:
      return { status: "in_progress", progress: null, error };
    case OrthoApprovalStatus.rejected:
      return { status: "failed", progress: null, error };
  }
};

/** Analysis state for one trial of the workflow's ortho. */
export interface TrialPlotTask {
  /** Latest task for the trial, if any. */
  task: TaskResponse | null;
  /** `pending` while the task is being started; null when there is no task. */
  status: TaskStatus | null;
  /** The start request is in flight. */
  isStarting: boolean;
  /** Waiting on snipping, pending, or processing. */
  isRunning: boolean;
  /** Can be started for the first time. */
  canStart: boolean;
  /** Failed or partially failed, and not already re-running. */
  canRetry: boolean;
  /** Task progress while processing, else null. */
  progress: number | null;
  /** Progress of the snip task this analysis is waiting on (0 if not reported yet), else null. */
  snipProgress: number | null;
  /** Why the latest task failed, else null. */
  failureMessage: string | null;
  /** Why the last start request for this trial failed (this tab only), else null. */
  startError: WorkflowError | null;
}

/** Analysis of the workflow's ortho, per trial. */
export interface WorkflowPlotTasks {
  /** Trials with a task or a start in flight. */
  byTrialId: Readonly<Record<number, TrialPlotTask>>;
  /** State of any trial not in `byTrialId`. */
  notStarted: TrialPlotTask;
}

/** Trial ids whose analyze start request is in flight, for one workflow. */
export interface StartingPlotTasks {
  analyzeEmergence: readonly number[];
  analyzeCanopy: readonly number[];
}

/** Trial ids whose analyze start request is in flight, by workflow. */
export type StartingPlotTasksByKey = Readonly<
  Partial<Record<OrthoWorkflowKey, StartingPlotTasks>>
>;

const getTrialPlotTask = ({
  task,
  snipTask,
  isStarting,
  canRun,
  taskLabel,
  startError,
}: {
  task: TaskResponse | null;
  /** Latest snip task for the trial, which the analyze task waits on. */
  snipTask: TaskResponse | null;
  isStarting: boolean;
  /** The tasks have loaded and the ortho is approved. */
  canRun: boolean;
  taskLabel: string;
  startError: WorkflowError | null;
}): TrialPlotTask => {
  const status = isStarting ? TaskStatus.pending : (task?.status ?? null);
  // Analyze tasks wait on the shared snip task before they are pending.
  const isRunning =
    status === TaskStatus.waiting ||
    status === TaskStatus.pending ||
    status === TaskStatus.processing;
  return {
    task,
    status,
    isStarting,
    isRunning,
    canStart: canRun && status == null,
    canRetry: canRun && isRetryableTask(task ?? undefined) && !isRunning,
    progress: task?.status === TaskStatus.processing ? task.progress : null,
    snipProgress:
      status === TaskStatus.waiting ? (snipTask?.progress ?? 0) : null,
    failureMessage: isRunning
      ? null
      : getRetryableTaskMessage(task ?? undefined, taskLabel),
    startError,
  };
};

/** For a trial of a workflow that isn't known yet: nothing can run. */
const NO_PLOT_TASK = getTrialPlotTask({
  task: null,
  snipTask: null,
  isStarting: false,
  canRun: false,
  taskLabel: "",
  startError: null,
});

/** Emergence analysis state of this workflow's ortho in `trialId`. */
export const getAnalyzeEmergenceForTrial = (
  workflow: OrthoWorkflow | null | undefined,
  trialId: number,
): TrialPlotTask =>
  workflow == null
    ? NO_PLOT_TASK
    : (workflow.analyzeEmergence.byTrialId[trialId] ??
      workflow.analyzeEmergence.notStarted);

/** Canopy cover analysis state of this workflow's ortho in `trialId`. */
export const getAnalyzeCanopyForTrial = (
  workflow: OrthoWorkflow | null | undefined,
  trialId: number,
): TrialPlotTask =>
  workflow == null
    ? NO_PLOT_TASK
    : (workflow.analyzeCanopy.byTrialId[trialId] ??
      workflow.analyzeCanopy.notStarted);

/** Newest start error per trial for an analyze step. */
const getStartErrorsByTrialId = (
  errors: readonly WorkflowError[],
  step: AnalyzeStepName,
): Map<number, WorkflowError> => {
  const byTrialId = new Map<number, WorkflowError>();
  for (const error of errors) {
    if (error.step !== step || error.trialId == null) {
      continue;
    }
    const current = byTrialId.get(error.trialId);
    if (current == null || error.at >= current.at) {
      byTrialId.set(error.trialId, error);
    }
  }
  return byTrialId;
};

/**
 * Per-trial analyze state. Nothing can start until that step's tasks have
 * loaded (null), so a start can't duplicate a task we haven't seen yet.
 */
const getWorkflowPlotTasks = (
  ortho: LocationOrtho | null,
  analyzeEmergenceTasks: Readonly<Record<number, TaskResponse>> | null,
  analyzeCanopyTasks: Readonly<Record<number, TaskResponse>> | null,
  snipTasks: Readonly<Record<number, TaskResponse>>,
  starting: StartingPlotTasks,
  clientErrors: readonly WorkflowError[],
): {
  analyzeEmergence: WorkflowPlotTasks;
  analyzeCanopy: WorkflowPlotTasks;
} => {
  const isApproved = ortho?.status === OrthoApprovalStatus.approved;

  const getAnalyzeTasks = (
    step: AnalyzeStepName,
    tasks: Readonly<Record<number, TaskResponse>> | null,
    startingTrialIds: readonly number[],
    taskLabel: string,
  ): WorkflowPlotTasks => {
    const startErrors = getStartErrorsByTrialId(clientErrors, step);
    const getAnalyze = (trialId: number | null) =>
      getTrialPlotTask({
        task: trialId != null ? (tasks?.[trialId] ?? null) : null,
        snipTask: trialId != null ? (snipTasks[trialId] ?? null) : null,
        isStarting: trialId != null && startingTrialIds.includes(trialId),
        canRun: isApproved && tasks != null,
        taskLabel,
        startError: trialId != null ? (startErrors.get(trialId) ?? null) : null,
      });
    const trialIds = new Set([
      ...Object.keys(tasks ?? {}).map(Number),
      ...startingTrialIds,
      ...startErrors.keys(),
    ]);
    const byTrialId: Record<number, TrialPlotTask> = {};
    for (const trialId of trialIds) {
      byTrialId[trialId] = getAnalyze(trialId);
    }
    return { byTrialId, notStarted: getAnalyze(null) };
  };

  return {
    analyzeEmergence: getAnalyzeTasks(
      "analyze_emergence",
      analyzeEmergenceTasks,
      starting.analyzeEmergence,
      "Emergence analysis",
    ),
    analyzeCanopy: getAnalyzeTasks(
      "analyze_canopy",
      analyzeCanopyTasks,
      starting.analyzeCanopy,
      "Canopy analysis",
    ),
  };
};

/** Groups plot tasks by the ortho they ran on; tasks without a summary yet are dropped. */
const groupTasksByOrthoId = (
  tasks: readonly TaskResponse[],
): Map<number, TaskResponse[]> => {
  const byOrthoId = new Map<number, TaskResponse[]>();
  for (const task of tasks) {
    const orthoId = getPlotTaskOrthoId(task);
    if (orthoId != null) {
      byOrthoId.set(orthoId, [...(byOrthoId.get(orthoId) ?? []), task]);
    }
  }
  return byOrthoId;
};

/** Newest task per trial id; tasks without a summary yet are dropped. */
const getLatestTasksByTrialId = (
  tasks: readonly TaskResponse[],
): Record<number, TaskResponse> => {
  const byTrialId: Record<number, TaskResponse> = {};
  for (const task of tasks) {
    const trialId = getPlotTaskTrialId(task);
    const current = trialId != null ? byTrialId[trialId] : undefined;
    if (
      trialId != null &&
      (current == null || task.created_at > current.created_at)
    ) {
      byTrialId[trialId] = task;
    }
  }
  return byTrialId;
};

// --- Build ---

const NOTHING_STARTING: StartingPlotTasks = {
  analyzeEmergence: [],
  analyzeCanopy: [],
};

export interface BuildOrthoWorkflowsInput {
  sessions: readonly OrthoUploadSession[];
  /** raw_image_upload tasks for this location. */
  uploadTasks: readonly TaskResponse[];
  /** ortho_creation tasks for this location. */
  creationTasks: readonly TaskResponse[];
  /**
   * analyze_emergence tasks; those for orthos that aren't in `orthos` are
   * ignored. Undefined until loaded, and no emergence analysis can start until then.
   */
  analyzeEmergenceTasks?: readonly TaskResponse[];
  /**
   * analyze_canopy_cover tasks; those for orthos that aren't in `orthos` are
   * ignored. Undefined until loaded, and no canopy analysis can start until then.
   */
  analyzeCanopyTasks?: readonly TaskResponse[];
  /** snip_ortho tasks, which analyze tasks wait on; those for other orthos are ignored. */
  snipOrthoTasks?: readonly TaskResponse[];
  orthos: readonly LocationOrtho[];
  /** ScaleODM status by ortho_creation task id. */
  liveStatus?: Readonly<Record<number, OrthoStatusResponse>>;
  orthoMeta?: Readonly<Record<number, ExistingOrthoMeta>>;
  clientErrors?: ClientErrorsByKey;
  startingPlotTasks?: StartingPlotTasksByKey;
}

interface WorkflowDraft extends WorkflowIdentity {
  creationTasks: TaskResponse[];
  orthos: LocationOrtho[];
  sessions: OrthoUploadSession[];
  uploadTasks: TaskResponse[];
}

/** The session in flight, else the most recent one. */
const pickSession = (
  sessions: readonly OrthoUploadSession[],
): OrthoUploadSession | null =>
  sessions.findLast(isInFlightSession) ?? sessions.at(-1) ?? null;

/** Each workflow has one ortho; the newest id wins if several were attached. */
const pickOrtho = (orthos: readonly LocationOrtho[]): LocationOrtho | null =>
  orthos.reduce<LocationOrtho | null>(
    (latest, ortho) =>
      latest == null || ortho.id > latest.id ? ortho : latest,
    null,
  );

/** Newest flight first; flights without a date yet (still uploading) on top. */
const compareWorkflows = (a: OrthoWorkflow, b: OrthoWorkflow): number => {
  if (a.flightDate !== b.flightDate) {
    if (a.flightDate == null) return -1;
    if (b.flightDate == null) return 1;
    return b.flightDate.localeCompare(a.flightDate);
  }
  return a.key.localeCompare(b.key);
};

const finalizeWorkflow = (
  draft: WorkflowDraft,
  {
    liveStatus,
    orthoMeta,
    analyzeEmergenceTasksByOrthoId,
    analyzeCanopyTasksByOrthoId,
    snipOrthoTasksByOrthoId,
    clientErrors,
    startingPlotTasks,
  }: {
    liveStatus: Readonly<Record<number, OrthoStatusResponse>>;
    orthoMeta: Readonly<Record<number, ExistingOrthoMeta>>;
    /** Null until loaded. */
    analyzeEmergenceTasksByOrthoId: ReadonlyMap<
      number,
      readonly TaskResponse[]
    > | null;
    /** Null until loaded. */
    analyzeCanopyTasksByOrthoId: ReadonlyMap<
      number,
      readonly TaskResponse[]
    > | null;
    /** Null until loaded. */
    snipOrthoTasksByOrthoId: ReadonlyMap<
      number,
      readonly TaskResponse[]
    > | null;
    clientErrors: readonly WorkflowError[];
    startingPlotTasks: StartingPlotTasks;
  },
): OrthoWorkflow => {
  const session = pickSession(draft.sessions);
  const ortho = pickOrtho(draft.orthos);
  // The open dialog can change site or flight time on this same workflow.
  const siteId = session?.selectedSiteId ?? draft.siteId;
  const flightDate =
    session?.flightDateTime != null
      ? formatFlightDate(session.flightDateTime)
      : draft.flightDate;
  const flightTime =
    session?.flightDateTime != null
      ? formatFlightTime(session.flightDateTime)
      : draft.flightTime;
  const creationTask = getLatestTask(draft.creationTasks);
  const live =
    creationTask?.status === TaskStatus.processing
      ? (liveStatus[creationTask.id] ?? null)
      : null;

  const tasks = draft.uploadTasks;
  const pendingTasks = tasks.filter(
    (task) => task.status === TaskStatus.pending,
  );
  const failedTasks = tasks.filter((task) => task.status === TaskStatus.failed);
  const completedCount = tasks.filter(
    (task) => task.status === TaskStatus.completed,
  ).length;
  const isUploading = isInFlightSession(session);
  const uploadedCount = isUploading
    ? (session?.uploadedFileCount ?? 0)
    : completedCount;
  const totalCount = isUploading
    ? (session?.totalFileCount ?? (tasks.length > 0 ? tasks.length : null))
    : tasks.length > 0
      ? tasks.length
      : null;

  const errors = [
    getUploadTasksError(failedTasks),
    getSessionError(session),
    ...getOrthoCreationTaskErrors(creationTask),
    ...clientErrors,
  ]
    .filter((error) => error != null)
    .sort(newestFirst);

  const getOrthoTasksByTrialId = (
    byOrthoId: ReadonlyMap<number, readonly TaskResponse[]> | null,
  ) =>
    byOrthoId != null
      ? getLatestTasksByTrialId(
          ortho != null ? (byOrthoId.get(ortho.id) ?? []) : [],
        )
      : null;
  const plotTasks = getWorkflowPlotTasks(
    ortho,
    getOrthoTasksByTrialId(analyzeEmergenceTasksByOrthoId),
    getOrthoTasksByTrialId(analyzeCanopyTasksByOrthoId),
    getOrthoTasksByTrialId(snipOrthoTasksByOrthoId) ?? {},
    startingPlotTasks,
    clientErrors,
  );

  const uploadProgress = isUploading
    ? totalCount != null && totalCount > 0
      ? (uploadedCount / totalCount) * 100
      : null
    : tasks.length > 0
      ? tasks.reduce((sum, task) => sum + task.progress, 0) / tasks.length
      : null;

  // Creation requested, or a stitched ortho: the upload step is done either way.
  const hasLeftUploading =
    ortho != null && ortho.status !== OrthoApprovalStatus.uploading;

  const stage = deriveStage({
    session,
    ortho,
    creationTask,
    pendingCount: pendingTasks.length,
    failedCount: failedTasks.length,
    completedCount,
  });

  return {
    key: draft.key,
    siteId,
    flightDate,
    flightTime,
    stage,
    steps: {
      upload: {
        status: getUploadStepStatus(
          session,
          tasks,
          pendingTasks.length,
          failedTasks.length,
          hasLeftUploading || creationTask != null,
        ),
        progress: uploadProgress,
        error: getNewestError(errors, "upload"),
      },
      orthoCreation: getOrthoCreationStep(
        ortho,
        creationTask,
        live,
        getNewestError(errors, "ortho_creation"),
        stage === "ortho_creation_failed" && creationTask == null,
      ),
      review: getReviewStep(ortho, getNewestError(errors, "review")),
    },
    errors,
    upload: {
      session,
      batches: groupTasksByFolder(tasks),
      uploadedCount,
      totalCount,
      pendingTasks,
      failedTasks,
    },
    orthoCreation: { task: creationTask, live },
    ortho,
    analyzeEmergence: plotTasks.analyzeEmergence,
    analyzeCanopy: plotTasks.analyzeCanopy,
    meta: (ortho != null ? orthoMeta[ortho.id] : undefined) ?? {},
  };
};

/**
 * One workflow per ortho. The ortho list is canonical. The only other workflow
 * is a new upload session in this tab, before it has been registered with the
 * API. Tasks join an ortho by `ortho_id`.
 */
export const buildOrthoWorkflows = ({
  sessions,
  uploadTasks,
  creationTasks,
  analyzeEmergenceTasks,
  analyzeCanopyTasks,
  snipOrthoTasks,
  orthos,
  liveStatus = {},
  orthoMeta = {},
  clientErrors = {},
  startingPlotTasks = {},
}: BuildOrthoWorkflowsInput): OrthoWorkflow[] => {
  const analyzeEmergenceTasksByOrthoId =
    analyzeEmergenceTasks != null
      ? groupTasksByOrthoId(analyzeEmergenceTasks)
      : null;
  const analyzeCanopyTasksByOrthoId =
    analyzeCanopyTasks != null ? groupTasksByOrthoId(analyzeCanopyTasks) : null;
  const snipOrthoTasksByOrthoId =
    snipOrthoTasks != null ? groupTasksByOrthoId(snipOrthoTasks) : null;

  const drafts = new Map<OrthoWorkflowKey, WorkflowDraft>();
  const getDraft = (identity: WorkflowIdentity): WorkflowDraft => {
    const existing = drafts.get(identity.key);
    if (existing != null) {
      return existing;
    }
    const draft: WorkflowDraft = {
      ...identity,
      creationTasks: [],
      orthos: [],
      sessions: [],
      uploadTasks: [],
    };
    drafts.set(identity.key, draft);
    return draft;
  };

  for (const ortho of orthos) {
    getDraft(identityForOrtho(ortho)).orthos.push(ortho);
  }
  for (const session of sessions) {
    const identity = identityForSession(session);
    if (identity != null) {
      getDraft(identity).sessions.push(session);
    }
  }
  const attachTask = (
    task: TaskResponse,
    bucket: "uploadTasks" | "creationTasks",
  ) => {
    const orthoId = getTaskOrthoId(task);
    if (orthoId == null) {
      return;
    }
    const draft = drafts.get(getWorkflowKey({ orthoId }));
    if (draft != null) {
      draft[bucket].push(task);
    }
  };
  for (const task of uploadTasks) {
    attachTask(task, "uploadTasks");
  }
  for (const task of creationTasks) {
    attachTask(task, "creationTasks");
  }

  return Array.from(drafts.values(), (draft) =>
    finalizeWorkflow(draft, {
      liveStatus,
      orthoMeta,
      analyzeEmergenceTasksByOrthoId,
      analyzeCanopyTasksByOrthoId,
      snipOrthoTasksByOrthoId,
      clientErrors: clientErrors[draft.key] ?? [],
      startingPlotTasks: startingPlotTasks[draft.key] ?? NOTHING_STARTING,
    }),
  ).sort(compareWorkflows);
};

// --- Queries over a workflow ---

export const isUploadInFlight = (workflow: OrthoWorkflow): boolean =>
  workflow.stage === "hashing" || workflow.stage === "uploading";

/** True once the server knows about the flight (tasks or ortho), or while its upload is in flight. */
export const hasServerRecord = (workflow: OrthoWorkflow): boolean =>
  workflow.upload.batches.length > 0 ||
  workflow.orthoCreation.task != null ||
  workflow.ortho != null ||
  isUploadInFlight(workflow);

/**
 * A new upload for this flight would duplicate an existing or in-progress ortho.
 * A rejected ortho does not count, even when its creation task completed.
 */
export const hasOrthoOrActiveCreation = (
  workflow: OrthoWorkflow | null | undefined,
): boolean =>
  workflow != null &&
  workflow.ortho?.status !== OrthoApprovalStatus.rejected &&
  (workflow.ortho != null ||
    (workflow.orthoCreation.task != null &&
      workflow.orthoCreation.task.status !== TaskStatus.failed));

/** True when some other workflow already has this site and flight instant. */
export const conflictsWithAnotherOrthoFlight = (
  workflows: readonly OrthoWorkflow[],
  flight: {
    flightDate: string;
    flightTime: string;
    /** The ortho being edited. It is not a conflict with itself. */
    orthoId?: number | null;
    siteId: number;
  },
): boolean =>
  workflows.some(
    (workflow) =>
      (flight.orthoId == null || workflow.ortho?.id !== flight.orthoId) &&
      workflow.siteId === flight.siteId &&
      workflow.flightDate === flight.flightDate &&
      workflow.flightTime === flight.flightTime &&
      hasOrthoOrActiveCreation(workflow),
  );

/**
 * Image uploads never registered for this placeholder, so the flight can be
 * uploaded again from scratch.
 */
export const canReuploadImages = (workflow: OrthoWorkflow): boolean =>
  workflow.ortho?.status === OrthoApprovalStatus.uploading &&
  workflow.orthoCreation.task == null &&
  workflow.upload.batches.length === 0 &&
  !isUploadInFlight(workflow);

/**
 * Original folder for this flight (`user_folder`). Uses the ortho field when
 * the list includes it, then the upload session, then the upload tasks.
 */
export const getWorkflowUserFolder = (
  workflow: OrthoWorkflow,
): string | null => {
  const fromOrtho = workflow.ortho?.user_folder?.trim() ?? "";
  if (fromOrtho !== "") {
    return fromOrtho;
  }

  const fromSession = workflow.upload.session?.folderPath?.trim() ?? "";
  if (fromSession !== "") {
    return fromSession;
  }

  const fromTasks =
    workflow.upload.batches
      .find((batch) => batch.folderPath.trim() !== "")
      ?.folderPath.trim() ?? "";
  return fromTasks === "" ? null : fromTasks;
};

/** Folder names in this workflow's uploads, for display. */
export const getWorkflowFolderNames = (workflow: OrthoWorkflow): string[] => {
  const folderPaths =
    workflow.upload.batches.length > 0
      ? workflow.upload.batches.map((batch) => batch.folderPath)
      : workflow.upload.session?.folderPath != null
        ? [workflow.upload.session.folderPath]
        : [];
  return folderPaths.map(
    (folderPath) => folderPath.split("/").filter(Boolean).pop() ?? "Root",
  );
};
